import serviceTypeCodes from 'constants/serviceTypeCodes';

export const isDirectFareLegs = (legs) => legs?.length === 1;
export const isThroughFareLegs = (legs) =>
  legs?.length > 1 && legs?.every((leg) => leg?.bookingItems?.every((item) => item.code === legs?.[0]?.bookingItems?.[0]?.code));
export const isSummatedFareLegs = (legs) =>
  legs?.length > 1 && legs?.some((leg) => leg?.bookingItems?.some((item) => item.code !== legs?.[0]?.bookingItems?.[0]?.code));

export const pendingLegsTotalPricePerCarrier = (legs, carrier = '') => {
  if (!carrier) {
    return legs?.reduce((acc, { bookingItems }) => acc + bookingItems?.reduce((accPrice, { price }) => accPrice + price, 0), 0);
  }

  const pendingLegs = legs?.filter((l) => l.serviceType.code === carrier);
  return pendingLegs?.reduce((acc, { bookingItems }) => acc + bookingItems?.reduce((accPrice, { price }) => accPrice + price, 0), 0);
};

export const pendingLegsTotalPricePerCarrierThrough = (legs, carrier = '') => {
  if (!carrier) {
    return legs?.reduce((acc, { products }) => acc + products?.reduce((accPrice, { originalPrice }) => accPrice + originalPrice, 0), 0);
  }

  const pendingLegs = legs?.filter((l) => l.serviceType.code === carrier);
  return pendingLegs?.reduce(
    (acc, { products }) => acc + products?.reduce((accPrice, { originalPrice }) => accPrice + originalPrice, 0),
    0
  );
};

export const retrievePricePerPaxAndCarrier = (passengerId, legs, carrier = '') => {
  if (!carrier) {
    return legs?.reduce(
      (acc, { bookingItems }) =>
        acc + bookingItems?.filter((item) => item.passengerId === passengerId)?.reduce((accPrice, { price }) => accPrice + price, 0),
      0
    );
  }

  const pendingLegs = legs?.filter((leg) => leg.serviceType.code === carrier);
  return pendingLegs?.reduce(
    (acc, { bookingItems }) =>
      acc + bookingItems?.filter((item) => item.passengerId === passengerId)?.reduce((accPrice, { price }) => accPrice + price, 0),
    0
  );
};

export const buildOverrideSummatedToThroughPrice = (createdLeg, cancellationLeg, isExchangingLessExpensive) => {
  const creationLegRebookedER = createdLeg?.filter((leg) => leg.serviceType?.code === serviceTypeCodes.GREENSPEED);
  const creationLegRebookedES = createdLeg?.filter((leg) => leg.serviceType?.code === serviceTypeCodes.EUROSTAR);

  // Always reprice ER to 0
  const erLegs = creationLegRebookedER
    ?.map(({ products }) =>
      products.map(({ itemRef }) => ({
        itemRefId: Number(itemRef),
        price: 0
      }))
    )
    ?.flat();

  // Reprice ES if the new legs are cheaper
  // To adds up to no refunds
  if (isExchangingLessExpensive) {
    const esLegs = creationLegRebookedES
      ?.map(({ products }) =>
        products.map(({ itemRef, passengerId }) => ({
          itemRefId: Number(itemRef),
          price: retrievePricePerPaxAndCarrier(passengerId, cancellationLeg)
        }))
      )
      ?.flat();

    return [...erLegs, ...esLegs];
  }

  return erLegs;
};

export const buildOverrideSummatedToDirectOrThroughPrice = (createdLeg, cancellationLeg) => {
  const creationLeg = createdLeg?.filter((leg) => leg.serviceType?.code === serviceTypeCodes.EUROSTAR);

  return creationLeg
    ?.map((leg) =>
      leg.bookingItems?.map((item) => ({
        itemRefId: Number(item.itemRef),
        price: retrievePricePerPaxAndCarrier(item.passengerId, cancellationLeg)
      }))
    )
    ?.flat();
};

export const buildOverrideDirectOrThroughToSummatedPrice = (createdLeg, cancellationLeg) => {
  const pendingCancellationFarePrice = pendingLegsTotalPricePerCarrier(cancellationLeg);
  const pendingCreationFarePrice = pendingLegsTotalPricePerCarrier(createdLeg);
  const creationLeg = createdLeg?.filter((leg) => leg.serviceType?.code === serviceTypeCodes.EUROSTAR);
  const passengerNumber = creationLeg?.map((leg) => leg.bookingItems?.map((l) => l.passengerId))?.flat()?.length;

  return creationLeg
    ?.map((leg) =>
      leg.bookingItems?.map((item) => ({
        itemRefId: Number(item.itemRef),
        price:
          retrievePricePerPaxAndCarrier(item.passengerId, createdLeg, serviceTypeCodes.EUROSTAR) +
          Math.max(0, (pendingCancellationFarePrice - pendingCreationFarePrice) / passengerNumber)
      }))
    )
    ?.flat();
};

export const buildOverrideSummatedToSummatedPrice = (createdLeg, cancellationLeg, carriers = []) => {
  if (carriers.length > 1) {
    const firstCarrierCreatedLeg = createdLeg?.filter((leg) => leg.serviceType?.code === carriers[0]);
    const firstCarrierCancelledLeg = cancellationLeg?.filter((leg) => leg.serviceType?.code === carriers[0]);
    const secondCarrierCreatedLeg = createdLeg?.filter((leg) => leg.serviceType?.code === carriers[1]);
    const secondCarrierCancelledLeg = cancellationLeg?.filter((leg) => leg.serviceType?.code === carriers[1]);

    return [
      ...(firstCarrierCreatedLeg ?? [])
        .map((leg) =>
          leg.bookingItems?.map((item) => ({
            itemRefId: Number(item.itemRef),
            price: retrievePricePerPaxAndCarrier(item.passengerId, firstCarrierCancelledLeg)
          }))
        )
        .flat(),
      ...(secondCarrierCreatedLeg ?? [])
        .map((leg) =>
          leg.bookingItems?.map((item) => ({
            itemRefId: Number(item.itemRef),
            price: retrievePricePerPaxAndCarrier(item.passengerId, secondCarrierCancelledLeg)
          }))
        )
        .flat()
    ];
  }

  const creationLeg = createdLeg?.filter((leg) => leg.serviceType?.code === carriers[0]);
  const cancelledLeg = cancellationLeg?.filter((leg) => leg.serviceType?.code === carriers[0]);

  return creationLeg
    ?.map((leg) =>
      leg.bookingItems?.map((item) => ({
        itemRefId: Number(item.itemRef),
        price: retrievePricePerPaxAndCarrier(item.passengerId, cancelledLeg)
      }))
    )
    ?.flat();
};

export const buildPercentageOfOriginalPrice = (createdLeg, cancellationLeg) => {
  const total = pendingLegsTotalPricePerCarrier(cancellationLeg);
  const createdLegsWithPercentagevalue = getPercentageValueOfNewSegment(createdLeg);

  return createdLegsWithPercentagevalue
    ?.map((leg) =>
      leg.bookingItems?.map((item) => ({
        itemRefId: Number(item.itemRef),
        price: (item.percentageValueOfTotal * total) / 100
      }))
    )
    ?.flat();
};

export const getPercentageValueOfNewSegment = (creationLegs) => {
  const total = pendingLegsTotalPricePerCarrier(creationLegs);

  return creationLegs
    ?.map(({ bookingItems, ...rest }) => ({
      ...rest,
      bookingItems: bookingItems?.map((bi) => ({ ...bi, percentageValueOfTotal: (bi?.price * 100) / total }))
    }))
    ?.flat();
};

export const overridePriceRequest = ({
  cancellationLeg,
  createdLeg,
  createdLegRebooked,
  isExchangingSummatedFare,
  isExchangingDirectFare,
  isExchangingThroughFare,
  isExchangingToSummatedFare,
  isExchangingToDirectFare,
  isExchangingToThroughFare
}) => {
  const pendingCancellationFarePrice = pendingLegsTotalPricePerCarrier(cancellationLeg);
  const pendingCreationFarePrice = pendingLegsTotalPricePerCarrier(createdLegRebooked);

  if (isExchangingSummatedFare) {
    if (isExchangingToDirectFare || isExchangingToThroughFare) {
      if (isExchangingToThroughFare) {
        // Extra check for train-inv reprice of ER
        const pendingCreationFarePriceThroughFare = pendingLegsTotalPricePerCarrierThrough(createdLeg?.legs);
        const creationLegRebookedER = createdLegRebooked?.filter((leg) => leg.serviceType?.code === serviceTypeCodes.GREENSPEED);
        const doesERRebookedHavePrice = creationLegRebookedER?.some(({ bookingItems }) => bookingItems?.some(({ price }) => price > 0));

        return pendingCancellationFarePrice > pendingCreationFarePriceThroughFare || doesERRebookedHavePrice
          ? buildOverrideSummatedToThroughPrice(
              createdLegRebooked,
              cancellationLeg,
              pendingCancellationFarePrice > pendingCreationFarePriceThroughFare
            )
          : [];
      }

      return pendingCancellationFarePrice > pendingCreationFarePrice
        ? buildOverrideSummatedToDirectOrThroughPrice(createdLegRebooked, cancellationLeg)
        : [];
    }

    if (isExchangingToSummatedFare) {
      const pendingCancellationESTrains = pendingLegsTotalPricePerCarrier(cancellationLeg, serviceTypeCodes.EUROSTAR);
      const pendingCancellationERTrains = pendingLegsTotalPricePerCarrier(cancellationLeg, serviceTypeCodes.GREENSPEED);
      const pendingCreationESTrains = pendingLegsTotalPricePerCarrier(createdLegRebooked, serviceTypeCodes.EUROSTAR);
      const pendingCreationERTrains = pendingLegsTotalPricePerCarrier(createdLegRebooked, serviceTypeCodes.GREENSPEED);

      if (pendingCancellationESTrains > pendingCreationESTrains && pendingCancellationERTrains > pendingCreationERTrains) {
        return buildOverrideSummatedToSummatedPrice(createdLegRebooked, cancellationLeg, [
          serviceTypeCodes.EUROSTAR,
          serviceTypeCodes.GREENSPEED
        ]);
      }

      if (pendingCancellationESTrains > pendingCreationESTrains) {
        return buildOverrideSummatedToSummatedPrice(createdLegRebooked, cancellationLeg, [serviceTypeCodes.EUROSTAR]);
      }

      if (pendingCancellationERTrains > pendingCreationERTrains) {
        return buildOverrideSummatedToSummatedPrice(createdLegRebooked, cancellationLeg, [serviceTypeCodes.GREENSPEED]);
      }

      return [];
    }

    return [];
  }

  if (isExchangingDirectFare || isExchangingThroughFare) {
    if (isExchangingToSummatedFare) {
      return pendingCancellationFarePrice > pendingCreationFarePrice
        ? buildOverrideDirectOrThroughToSummatedPrice(createdLegRebooked, cancellationLeg)
        : [];
    }
    return [];
  }

  return [];
};

export const overrideSummatedFareDifferencePriceRequest = ({
  cancellationLeg,
  createdLeg,
  isExchangingSummatedFare,
  isExchangingDirectFare,
  isExchangingThroughFare,
  isExchangingToSummatedFare,
  isExchangingToDirectFare,
  isExchangingToThroughFare
}) => {
  const pendingCancellationFarePrice = pendingLegsTotalPricePerCarrier(cancellationLeg);
  const pendingCreationFarePrice = pendingLegsTotalPricePerCarrier(createdLeg);

  if (isExchangingSummatedFare && pendingCreationFarePrice > pendingCancellationFarePrice) {
    if (isExchangingToDirectFare || isExchangingToThroughFare) {
      return buildOverrideSummatedToDirectOrThroughPrice(createdLeg, cancellationLeg);
    }

    if (isExchangingToSummatedFare) {
      return buildOverrideSummatedToSummatedPrice(createdLeg, cancellationLeg, [serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED]);
    }

    return [];
  }

  if (isExchangingDirectFare || isExchangingThroughFare) {
    if (isExchangingToSummatedFare && pendingCreationFarePrice > pendingCancellationFarePrice) {
      return buildPercentageOfOriginalPrice(createdLeg, cancellationLeg);
    }

    return [];
  }

  return [];
};
